<template>
  <div :class="[themeClasses.pageContainer, 'p-4']">
    <h4 :class="[themeClasses.cardTitle, 'mb-4']">Gestión de Configuraciones del SRI</h4>

    <ul class="nav nav-tabs mb-4" role="tablist">
      <li class="nav-item" role="presentation">
        <button class="nav-link" :class="{ active: activeTab === 'impuestos' }" type="button" role="tab" @click="activeTab = 'impuestos'">
          Códigos de Impuesto
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button class="nav-link" :class="{ active: activeTab === 'identificacion' }" type="button" role="tab" @click="activeTab = 'identificacion'">
          Tipos de Identificación
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button class="nav-link" :class="{ active: activeTab === 'otros' }" type="button" role="tab" @click="activeTab = 'otros'">
          Otras Configuraciones
        </button>
      </li>
    </ul>

    <section v-show="activeTab === 'impuestos'" class="mb-5">
      <header class="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-3 align-items-lg-center">
        <div>
          <h5 :class="themeClasses.headingText" class="mb-1">Códigos de Impuesto (SRI)</h5>
          <p :class="['mb-0', themeClasses.mutedText]">Activa la edición por fila y confirma todos los cambios con el botón general.</p>
        </div>
        <div class="d-flex flex-column flex-sm-row gap-2 align-items-sm-center">
          <small :class="themeClasses.mutedText">Cambios pendientes: {{ pendingTaxEdits }}</small>
          <button :class="themeClasses.buttonPrimary + ' btn-sm d-flex align-items-center gap-2'" :disabled="!hasTaxChanges || savingTaxes" @click="saveAllTaxChanges">
            <span v-if="savingTaxes" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span>Guardar cambios</span>
          </button>
        </div>
      </header>

      <div v-if="loadingTaxes" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando impuestos...</span>
        </div>
        <p class="text-muted mt-2">Cargando códigos de impuesto...</p>
      </div>
      <div v-else>
        <div class="table-responsive">
          <table :class="[themeClasses.tableBordered, 'table-sm', 'align-middle']">
            <thead class="text-center small theme-table-header">
              <tr>
                <th class="text-uppercase">Código</th>
                <th class="text-uppercase">Descripción</th>
                <th class="text-uppercase">Tasa (%)</th>
                <th class="text-uppercase text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="taxCodes.length === 0">
                <td colspan="4" class="text-center text-muted">No hay códigos de impuesto registrados.</td>
              </tr>
              <tr v-for="tax in taxCodes" :key="tax.code">
                <td class="text-center fw-semibold">{{ tax.code }}</td>
                <td>
                  <template v-if="isTaxEditing(tax.code)">
                    <input v-model.trim="taxDrafts[tax.code].description" type="text" :class="[themeClasses.formControl, 'form-control-sm']" @input="markTaxDirty(tax.code)" />
                  </template>
                  <span v-else>{{ tax.description }}</span>
                </td>
                <td class="text-center">
                  <template v-if="isTaxEditing(tax.code)">
                    <input v-model.number="taxDrafts[tax.code].rate" type="number" step="0.01" min="0" :class="[themeClasses.formControl, 'form-control-sm', 'text-center']" @input="markTaxDirty(tax.code)" />
                  </template>
                  <span v-else>{{ formatRate(tax.rate) }}</span>
                </td>
                <td class="text-center">
                  <div class="action-icons">
                    <button class="btn btn-sm btn-outline-secondary" @click="toggleTaxEdit(tax)" :aria-label="isTaxEditing(tax.code) ? 'Cancelar edición' : 'Editar impuesto'">
                      <i :class="isTaxEditing(tax.code) ? 'bi bi-x-circle' : 'bi bi-pencil'" aria-hidden="true"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="text-muted small mt-3">
          Estas tasas se sincronizan con los documentos electrónicos y cálculos tributarios del sistema. Los códigos emitidos por el SRI no pueden ser modificados.
        </p>
      </div>
    </section>

    <section v-show="activeTab === 'identificacion'" class="mb-5">
      <header class="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-3 align-items-lg-center">
        <div>
          <h5 :class="themeClasses.headingText" class="mb-1">Tipos de Identificación (SRI)</h5>
          <p :class="['mb-0', themeClasses.mutedText]">Activa la edición por fila y confirma todos los cambios con el botón general.</p>
        </div>
        <div class="d-flex flex-column flex-sm-row gap-2 align-items-sm-center">
          <small :class="themeClasses.mutedText">Cambios pendientes: {{ pendingTypeEdits }}</small>
          <button :class="themeClasses.buttonPrimary + ' btn-sm d-flex align-items-center gap-2'" :disabled="!hasTypeChanges || savingTypes" @click="saveAllTypeChanges">
            <span v-if="savingTypes" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span>Guardar cambios</span>
          </button>
        </div>
      </header>

      <div v-if="loadingTypes" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando tipos...</span>
        </div>
        <p class="text-muted mt-2">Cargando tipos de identificación SRI...</p>
      </div>
      <div v-else>
        <div class="table-responsive mb-3">
          <table :class="[themeClasses.tableBordered, 'table-sm', 'align-middle']">
            <thead class="text-center small theme-table-header">
              <tr>
                <th class="text-uppercase text-center">Activo</th>
                <th class="text-uppercase">Código</th>
                <th class="text-uppercase">Nombre</th>
                <th class="text-uppercase">Descripción</th>
                <th class="text-uppercase">Patrón</th>
                <th class="text-uppercase">Min</th>
                <th class="text-uppercase">Max</th>
                <th class="text-uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="types.length === 0">
                <td colspan="8" class="text-center text-muted">No hay tipos registrados.</td>
              </tr>
              <tr v-for="type in types" :key="type.code">
                <td class="text-center sri-min">
                  <template v-if="isTypeEditing && isTypeEditing(type.code)">
                    <input type="checkbox" class="form-check-input" v-model="typeDrafts[type.code].is_active" @change="markTypeDirty(type.code)" />
                  </template>
                  <input v-else type="checkbox" class="form-check-input" :checked="type.is_active" disabled />
                </td>
                <td class="fw-semibold">{{ type.code }}</td>
                <td>
                  <template v-if="isTypeEditing && isTypeEditing(type.code)">
                    <input v-model="typeDrafts[type.code].name" type="text" :class="[themeClasses.formControl, 'form-control-sm']" @input="markTypeDirty(type.code)" />
                  </template>
                  <span v-else>{{ type.name }}</span>
                </td>
                <td>
                  <template v-if="isTypeEditing && isTypeEditing(type.code)">
                    <input v-model="typeDrafts[type.code].description" type="text" :class="[themeClasses.formControl, 'form-control-sm']" @input="markTypeDirty(type.code)" />
                  </template>
                  <span v-else>{{ type.description }}</span>
                </td>
                <td>
                  <template v-if="isTypeEditing && isTypeEditing(type.code)">
                    <input v-model="typeDrafts[type.code].validation_pattern" type="text" :class="[themeClasses.formControl, 'form-control-sm']" @input="markTypeDirty(type.code)" />
                  </template>
                  <span v-else>{{ type.validation_pattern || '—' }}</span>
                </td>
                <td class="text-center sri-max">
                  <template v-if="isTypeEditing && isTypeEditing(type.code)">
                    <input v-model.number="typeDrafts[type.code].min_length" type="number" min="1" :class="[themeClasses.formControl, 'form-control-sm', 'text-center']" @input="markTypeDirty(type.code)" />
                  </template>
                  <span v-else>{{ type.min_length }}</span>
                </td>
                <td class="text-center sri-actions">
                  <template v-if="isTypeEditing && isTypeEditing(type.code)">
                    <input v-model.number="typeDrafts[type.code].max_length" type="number" min="1" :class="[themeClasses.formControl, 'form-control-sm', 'text-center']" @input="markTypeDirty(type.code)" />
                  </template>
                  <span v-else>{{ type.max_length }}</span>
                </td>
                <td class="text-center">
                  <div class="action-icons">
                    <button class="btn btn-sm btn-outline-secondary" @click="toggleTypeEdit(type)" :aria-label="isTypeEditing && isTypeEditing(type.code) ? 'Cancelar edición' : 'Editar tipo'">
                      <i :class="isTypeEditing && isTypeEditing(type.code) ? 'bi bi-x-circle' : 'bi bi-pencil'" aria-hidden="true"></i>
                    </button>
                    <button :class="themeClasses.buttonDanger + ' btn-sm'" @click="deleteType(type)" aria-label="Eliminar tipo">
                      <i class="bi bi-trash" aria-hidden="true"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <form class="row g-2 align-items-end" @submit.prevent="addType">
          <div class="col-sm-6 col-md-2">
            <input v-model="newType.code" type="text" maxlength="2" placeholder="Código" required :class="[themeClasses.formControl, 'form-control-sm']" />
          </div>
          <div class="col-sm-6 col-md-2">
            <input v-model="newType.name" type="text" placeholder="Nombre" required :class="[themeClasses.formControl, 'form-control-sm']" />
          </div>
          <div class="col-sm-6 col-md-2">
            <input v-model="newType.description" type="text" placeholder="Descripción" :class="[themeClasses.formControl, 'form-control-sm']" />
          </div>
          <div class="col-sm-6 col-md-2">
            <input v-model="newType.validation_pattern" type="text" placeholder="Patrón" :class="[themeClasses.formControl, 'form-control-sm']" />
          </div>
          <div class="col-6 col-md-1">
            <input v-model.number="newType.min_length" type="number" min="1" placeholder="Min" :class="[themeClasses.formControl, 'form-control-sm']" />
          </div>
          <div class="col-6 col-md-1">
            <input v-model.number="newType.max_length" type="number" min="1" placeholder="Max" :class="[themeClasses.formControl, 'form-control-sm']" />
          </div>
          <div class="col-md-2">
            <button :class="themeClasses.buttonPrimary + ' btn-sm w-100'" type="submit">Agregar</button>
          </div>
        </form>
      </div>
    </section>

    <section v-show="activeTab === 'otros'">
      <h5 :class="themeClasses.headingText">Otras configuraciones del SRI</h5>
      <p :class="themeClasses.mutedText">Próximamente podrás ajustar parámetros adicionales como secuenciales y firmas electrónicas desde este panel.</p>
    </section>
  </div>
</template>

<script>
import api from '@/services/api';
import themeMixin from '@/mixins/themeMixin';

export default {
  name: 'ConfiguracionSri',
  mixins: [themeMixin],
  data() {
    return {
      activeTab: 'impuestos',
      loadingTaxes: false,
      loadingTypes: false,
      taxCodes: [],
      types: [],
      taxDrafts: {},
      typeDrafts: {},
      savingTaxes: false,
      savingTypes: false,
      newType: {
        code: '',
        name: '',
        description: '',
        validation_pattern: '',
        min_length: 1,
        max_length: 1
      }
    };
  },
  computed: {
    hasTaxChanges() {
      return Object.values(this.taxDrafts).some((draft) => draft?.dirty);
    },
    hasTypeChanges() {
      return Object.values(this.typeDrafts).some((draft) => draft?.dirty);
    },
    pendingTaxEdits() {
      return Object.keys(this.taxDrafts).length;
    },
    pendingTypeEdits() {
      return Object.keys(this.typeDrafts).length;
    }
  },
  methods: {
    isTaxEditing(code) {
      return !!this.taxDrafts[code];
    },
    isTypeEditing(code) {
      return !!this.typeDrafts[code];
    },
    handleFetchError(err, context = '') {
      console.error(`[SRI] Error ${context}:`, err);
      const serverMsg = err?.response?.data?.error || err?.response?.data?.message;
      const msg = serverMsg || err?.message || 'Error desconocido';
      window.Swal.fire({
        title: `Error ${context ? '(' + context + ')' : ''}`,
        text: msg,
        icon: 'error',
        confirmButtonText: 'Entendido'
      })
    },
    async fetchSriTaxCodes() {
      this.loadingTaxes = true;
      try {
        const { data } = await api.get('/sri-tax-codes');
        this.taxCodes = Array.isArray(data?.data)
          ? data.data.map((tax) => ({ ...tax, rate: Number(tax.rate) }))
          : [];
        this.taxDrafts = {};
      } catch (error) {
        this.handleFetchError(error, 'al cargar códigos de impuesto SRI');
        this.taxCodes = [];
      } finally {
        this.loadingTaxes = false;
      }
    },
    toggleTaxEdit(tax) {
      if (this.taxDrafts[tax.code]) {
        const drafts = { ...this.taxDrafts };
        delete drafts[tax.code];
        this.taxDrafts = drafts;
        return;
      }
      this.taxDrafts = {
        ...this.taxDrafts,
        [tax.code]: {
          description: tax.description,
          rate: Number(tax.rate),
          dirty: false
        }
      };
    },
    markTaxDirty(code) {
      if (this.taxDrafts[code]) {
        this.taxDrafts[code].dirty = true;
      }
    },
    async saveAllTaxChanges() {
      const pending = Object.entries(this.taxDrafts).filter(([, draft]) => draft?.dirty);
      if (pending.length === 0) {
        window.Swal.fire({
          title: 'Sin Cambios',
          text: 'No hay cambios por guardar en los códigos de impuesto.',
          icon: 'info',
          confirmButtonText: 'Entendido'
        })
        return;
      }
      this.savingTaxes = true;
      try {
        for (const [code, draft] of pending) {
          if (!draft.description || !draft.description.trim()) {
            throw new Error(`La descripción del impuesto ${code} no puede estar vacía.`);
          }
          const rateValue = Number(draft.rate);
          if (Number.isNaN(rateValue) || rateValue < 0) {
            throw new Error(`La tasa del impuesto ${code} debe ser mayor o igual a 0.`);
          }
          await api.put(`/sri-tax-codes/${encodeURIComponent(code)}`, {
            description: draft.description.trim(),
            rate: rateValue
          });
        }
        await this.fetchSriTaxCodes();
        this.taxDrafts = {};
        window.Swal.fire({
          title: 'Cambios Guardados',
          text: 'Cambios en códigos de impuesto guardados correctamente.',
          icon: 'success',
          confirmButtonText: 'Entendido'
        })
      } catch (error) {
        this.handleFetchError(error, 'al guardar cambios de impuestos');
      } finally {
        this.savingTaxes = false;
      }
    },
    formatRate(value) {
      const num = Number(value);
      return Number.isNaN(num) ? '0.00' : num.toFixed(2);
    },
    async fetchSriTypes() {
      this.loadingTypes = true;
      try {
        const { data } = await api.get('/sri-identification-types');
        this.types = Array.isArray(data?.data)
          ? data.data.map((t) => ({ ...t, is_active: !!t.is_active }))
          : [];
        this.typeDrafts = {};
      } catch (error) {
        this.handleFetchError(error, 'al cargar tipos SRI');
        this.types = [];
      } finally {
        this.loadingTypes = false;
      }
    },
    toggleTypeEdit(type) {
      if (this.typeDrafts[type.code]) {
        const drafts = { ...this.typeDrafts };
        delete drafts[type.code];
        this.typeDrafts = drafts;
        return;
      }
      this.typeDrafts = {
        ...this.typeDrafts,
        [type.code]: {
          name: type.name,
          description: type.description,
          validation_pattern: type.validation_pattern,
          min_length: Number(type.min_length),
          max_length: Number(type.max_length),
          is_active: !!type.is_active,
          dirty: false
        }
      };
    },
    markTypeDirty(code) {
      if (this.typeDrafts[code]) {
        this.typeDrafts[code].dirty = true;
      }
    },
    async saveAllTypeChanges() {
      const pending = Object.entries(this.typeDrafts).filter(([, draft]) => draft?.dirty);
      if (pending.length === 0) {
        window.Swal.fire({
          title: 'Sin Cambios',
          text: 'No hay cambios por guardar en los tipos de identificación.',
          icon: 'info',
          confirmButtonText: 'Entendido'
        })
        return;
      }
      this.savingTypes = true;
      try {
        for (const [code, draft] of pending) {
          if (!draft.name || !draft.name.trim()) {
            throw new Error(`El nombre del tipo ${code} es obligatorio.`);
          }
          const minLen = Number(draft.min_length);
          const maxLen = Number(draft.max_length);
          if (Number.isNaN(minLen) || Number.isNaN(maxLen) || minLen <= 0 || maxLen <= 0 || minLen > maxLen) {
            throw new Error(`Las longitudes del tipo ${code} no son válidas.`);
          }
          await api.put(`/sri-identification-types/${encodeURIComponent(code)}`, {
            name: draft.name.trim(),
            description: draft.description?.trim() || '',
            validation_pattern: draft.validation_pattern?.trim() || '',
            min_length: minLen,
            max_length: maxLen,
            is_active: draft.is_active ? 1 : 0
          });
        }
        await this.fetchSriTypes();
        this.typeDrafts = {};
        window.Swal.fire({
          title: 'Cambios Guardados',
          text: 'Cambios en tipos de identificación guardados correctamente.',
          icon: 'success',
          confirmButtonText: 'Entendido'
        })
      } catch (error) {
        this.handleFetchError(error, 'al guardar cambios de tipos');
      } finally {
        this.savingTypes = false;
      }
    },
    async addType() {
      try {
        await api.post('/sri-identification-types', this.newType);
        this.newType = {
          code: '',
          name: '',
          description: '',
          validation_pattern: '',
          min_length: 1,
          max_length: 1
        };
        await this.fetchSriTypes();
      } catch (error) {
        this.handleFetchError(error, 'al agregar tipo');
      }
    },
    async deleteType(type) {
      const result = await window.Swal.fire({
        title: 'Eliminar Tipo',
        text: `¿Eliminar tipo ${type.name}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      })

      if (!result.isConfirmed) return

      try {
        await api.delete(`/sri-identification-types/${encodeURIComponent(type.code)}`);
        if (this.typeDrafts[type.code]) {
          const drafts = { ...this.typeDrafts };
          delete drafts[type.code];
          this.typeDrafts = drafts;
        }
        await this.fetchSriTypes();
      } catch (error) {
        this.handleFetchError(error, 'al eliminar tipo');
      }
    }
  },
  mounted() {
    this.fetchSriTaxCodes();
    this.fetchSriTypes();
  }
};
</script>

<style>
table.sri-table {
  table-layout: fixed;
  width: 100%;
}

table.sri-table th,
table.sri-table td {
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle !important;
  padding: 0.25rem 0.5rem;
  font-size: 0.95rem;
  height: 40px;
}

table.sri-table th.sri-min,
table.sri-table td.sri-min {
  width: 70px;
  max-width: 70px;
  text-align: center;
}
table.sri-table th.sri-max,
table.sri-table td.sri-max {
  width: 70px;
  max-width: 70px;
  text-align: center;
}
table.sri-table th.sri-actions,
table.sri-table td.sri-actions {
  width: 90px;
  max-width: 90px;
  text-align: center;
}

.action-icons {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
table.sri-table td.sri-max {
  max-width: 100px;
}

table.sri-table td.sri-actions {
  text-align: center;
  min-width: 80px;
}

.action-icons button {
  min-width: 32px;
  height: 32px;
  margin: 0 2px;
  vertical-align: middle;
}

table.sri-table td input,
table.sri-table td span {
  display: inline-block;
  vertical-align: middle;
  white-space: nowrap !important;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
table.sri-table th,
table.sri-table td {
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle !important;
  padding: 0.25rem 0.5rem;
  font-size: 0.95rem;
  height: 40px;
  max-width: 120px;
}

table.sri-table td.sri-max {
  white-space: nowrap !important;
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle !important;
}

table.sri-table td.sri-actions {
  white-space: nowrap !important;
  vertical-align: middle !important;
  text-align: center;
}

table.sri-table td input,
table.sri-table td span {
  display: inline-block;
  vertical-align: middle;
  white-space: nowrap !important;
  max-width: 100px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
h4 {
  font-size: 1.5rem;
  letter-spacing: 0.8px;
}

table.sri-table th,
table.sri-table td {
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  padding: 0.25rem 0.5rem;
  font-size: 0.95rem;
  height: 40px;
  max-width: 120px;
}

table.sri-table td.sri-actions {
  white-space: nowrap !important;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  justify-content: center;
  height: 40px;
}

table.sri-table td.sri-min,
table.sri-table td.sri-max {
  white-space: nowrap !important;
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 40px;
}

table.sri-table td input,
table.sri-table td span {
  white-space: nowrap !important;
  max-width: 100px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  vertical-align: middle;
}

.theme-table-header th {
  letter-spacing: 0.05em;
  font-size: 0.75rem;
  white-space: nowrap;
}

section + section {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  padding-top: 1.5rem;
}

.table-responsive {
  overflow-x: auto;
}
</style>
